import React, { useEffect, useMemo, useState } from 'react';
import { fetchDistrictAvailability } from '../data/api';
import DistrictStoreModal from './DistrictStoreModal';

const TYPE_LABEL = {
  ddwh: 'DDWH (District Drug Warehouses)',
  mc:   'Medical Colleges',
  dh:   'District Hospitals',
  sdh:  'SDH (Sub-Divisional Hospitals)',
  chc:  'CHC/MH (CHC + Model Hospitals)',
  phc:  'PHC & Sub-Centres',
  all:  'All Facilities',
};

const statusBadge = (v) => {
  const val = parseFloat(v) || 0;
  let bg, fg, label;
  if (val >= 90)      { bg = '#dcfce7'; fg = '#15803d'; label = 'Good'; }
  else if (val >= 75) { bg = '#fef3c7'; fg = '#b45309'; label = 'Moderate'; }
  else if (val >= 50) { bg = '#ffedd5'; fg = '#c2410c'; label = 'Low'; }
  else                { bg = '#fee2e2'; fg = '#b91c1c'; label = 'Critical'; }
  return (
    <span style={{
      background: bg, color: fg, fontWeight: 700,
      borderRadius: 12, padding: '2px 10px', fontSize: '.7rem'
    }}>{label}</span>
  );
};

export default function FacilityListModal({ facType, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('avail');
  const [sortDir, setSortDir] = useState('desc');
  const [drillDistrict, setDrillDistrict] = useState(null);

  useEffect(() => {
    if (!facType) return;
    setLoading(true);
    // For "all" — request store-level data (1325 individual stores), not district aggregates
    fetchDistrictAvailability(facType === 'all' ? '' : facType, facType === 'all' ? 'level=store' : '')
      .then(r => { setRows(r.data || []); setLoading(false); })
      .catch(() => { setRows([]); setLoading(false); });
  }, [facType]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir(field === 'avail' ? 'desc' : 'asc'); }
  };

  const filtered = useMemo(() => {
    let list = [...rows];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(r =>
        (r.n || '').toLowerCase().includes(q) ||
        (r.district || '').toLowerCase().includes(q)
      );
    }
    if (filterStatus !== 'all') {
      list = list.filter(r => {
        const v = parseFloat(r.avail) || 0;
        if (filterStatus === 'good') return v >= 90;
        if (filterStatus === 'moderate') return v >= 75 && v < 90;
        if (filterStatus === 'low') return v >= 50 && v < 75;
        if (filterStatus === 'critical') return v < 50;
        return true;
      });
    }
    list.sort((a, b) => {
      let av, bv;
      if (sortField === 'name') { av = (a.n || '').toLowerCase(); bv = (b.n || '').toLowerCase(); }
      else if (sortField === 'district') { av = (a.district || '').toLowerCase(); bv = (b.district || '').toLowerCase(); }
      else if (sortField === 'total') { av = parseInt(a.total_stores) || 0; bv = parseInt(b.total_stores) || 0; }
      else if (sortField === 'inStock') { av = parseInt(a.stores_above_90) || 0; bv = parseInt(b.stores_above_90) || 0; }
      else if (sortField === 'stockout') { av = parseInt(a.stockout_stores) || 0; bv = parseInt(b.stockout_stores) || 0; }
      else { av = parseFloat(a.avail) || 0; bv = parseFloat(b.avail) || 0; }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [rows, search, filterStatus, sortField, sortDir]);

  // Summary stats
  const stats = useMemo(() => {
    const total = filtered.length;
    const good = filtered.filter(r => parseFloat(r.avail) >= 90).length;
    const moderate = filtered.filter(r => { const v = parseFloat(r.avail); return v >= 75 && v < 90; }).length;
    const low = filtered.filter(r => { const v = parseFloat(r.avail); return v >= 50 && v < 75; }).length;
    const critical = filtered.filter(r => parseFloat(r.avail) < 50).length;
    const avgAvail = total > 0 ? (filtered.reduce((s, r) => s + parseFloat(r.avail || 0), 0) / total).toFixed(1) : 0;
    return { total, good, moderate, low, critical, avgAvail };
  }, [filtered]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const isAll = facType === 'all';
    const headers = isAll
      ? ['#', 'Store Name', 'District', 'Facility Type', 'Avail %']
      : ['#', 'Name', 'District', 'Total Items', 'In Stock', 'Stockouts', 'Avail %'];
    const rows = filtered.map((r, i) => isAll
      ? [i + 1, r.n, (r.district || '').trim(), r.facility_type || '', parseFloat(r.avail).toFixed(2)]
      : [i + 1, r.n, r.district || '',
         r.total_stores || 0, r.stores_above_90 || 0,
         r.stockout_stores || 0, parseFloat(r.avail).toFixed(2)]
    );
    const csv = [headers, ...rows].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${facType}_facilities.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const thStyle = {
    padding: '10px 12px', fontWeight: 700, fontSize: '.72rem',
    color: '#475569', borderBottom: '2px solid #e2e8f0',
    textAlign: 'left', cursor: 'pointer', userSelect: 'none',
    whiteSpace: 'nowrap'
  };
  const tdStyle = { padding: '8px 12px', fontSize: '.78rem', color: '#1e293b' };

  if (!facType) return null;

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
        backdropFilter: 'blur(2px)', zIndex: 9000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          background: '#fff', borderRadius: 14, width: '95%', maxWidth: 1100,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,.25)', overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 22px', borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #0f2744 0%, #1e3a5f 100%)',
            color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
                {TYPE_LABEL[facType] || facType.toUpperCase()}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: '.78rem', opacity: .85 }}>
                {loading ? 'Loading...' : `${stats.total} facilities • Avg: ${stats.avgAvail}%`}
              </p>
            </div>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,.15)', color: '#fff', border: 0,
              width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
              fontSize: '1.1rem', fontWeight: 700
            }}>×</button>
          </div>

          {/* Stats bar */}
          <div style={{
            padding: '10px 22px', display: 'flex', gap: 18,
            background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
            fontSize: '.78rem', flexWrap: 'wrap'
          }}>
            <span><b style={{ color: '#15803d' }}>{stats.good}</b> Good</span>
            <span><b style={{ color: '#b45309' }}>{stats.moderate}</b> Moderate</span>
            <span><b style={{ color: '#c2410c' }}>{stats.low}</b> Low</span>
            <span><b style={{ color: '#b91c1c' }}>{stats.critical}</b> Critical</span>
          </div>

          {/* Filters */}
          <div style={{
            padding: '12px 22px', display: 'flex', gap: 10,
            borderBottom: '1px solid #f1f5f9', alignItems: 'center', flexWrap: 'wrap'
          }}>
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search store or district..."
              style={{
                flex: 1, minWidth: 200, padding: '8px 12px',
                border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '.82rem'
              }}
            />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '.82rem' }}>
              <option value="all">All Status</option>
              <option value="good">✅ Good (≥90%)</option>
              <option value="moderate">⚠️ Moderate (75-89%)</option>
              <option value="low">🔶 Low (50-74%)</option>
              <option value="critical">🔴 Critical (&lt;50%)</option>
            </select>
            <button onClick={exportCsv} style={{
              padding: '8px 14px', background: '#10b981', color: '#fff',
              border: 0, borderRadius: 8, fontSize: '.78rem', fontWeight: 600, cursor: 'pointer'
            }}>📥 CSV</button>
          </div>

          {/* Table */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No facilities found</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 2 }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle} onClick={() => toggleSort('name')}>
                      {facType === 'all' ? 'Store Name' : 'Facility Name'} {sortField === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th style={thStyle} onClick={() => toggleSort('district')}>
                      District {sortField === 'district' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    {facType === 'all' ? (
                      <th style={thStyle}>Facility Type</th>
                    ) : (
                      <>
                        <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('total')}>
                          Total Items {sortField === 'total' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('inStock')}>
                          In Stock {sortField === 'inStock' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('stockout')}>
                          Stockouts {sortField === 'stockout' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      </>
                    )}
                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('avail')}>
                      Avail % {sortField === 'avail' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const v = parseFloat(r.avail) || 0;
                    return (
                      <tr key={i}
                        onClick={() => r.district && setDrillDistrict(r.district.trim())}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: i % 2 === 0 ? '#fff' : '#fafbfc',
                          cursor: r.district ? 'pointer' : 'default',
                          transition: 'background 0.15s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#f0f7ff'}
                        onMouseOut={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                      >
                        <td style={{ ...tdStyle, color: '#94a3b8', fontWeight: 600, width: 36 }}>{i + 1}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{r.n}</td>
                        <td style={{ ...tdStyle, color: '#475569' }}>{(r.district || '').trim() || '-'}</td>
                        {facType === 'all' ? (
                          <td style={{ ...tdStyle }}>
                            <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, fontSize: '.7rem', fontWeight: 600, color: '#475569' }}>
                              {r.facility_type || '-'}
                            </span>
                          </td>
                        ) : (
                          <>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{r.total_stores || 0}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#15803d', fontWeight: 600 }}>
                              {r.stores_above_90 || 0}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: parseInt(r.stockout_stores) > 0 ? '#b91c1c' : '#94a3b8', fontWeight: 600 }}>
                              {r.stockout_stores || 0}
                            </td>
                          </>
                        )}
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: v >= 90 ? '#15803d' : v >= 75 ? '#b45309' : '#b91c1c' }}>
                          {v.toFixed(2)}%
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{statusBadge(v)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Drill-down modal */}
      {drillDistrict && (
        <DistrictStoreModal
          district={drillDistrict}
          facType={facType === 'all' ? 'all' : facType}
          onClose={() => setDrillDistrict(null)}
        />
      )}
    </>
  );
}
